import {
  Clock,
  Download,
  Eye,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Loader2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { isPreviewableFile, openDocumentPreview } from "@/components/chat/DocumentPreviewModal";
import { subscribeVersionPanel, type VersionPanelRequest } from "@/components/chat/documentVersionEvents";
import { getApiBaseUrl } from "@/config/runtime";
import { api, getHeaders } from "@/lib/api";
import type { DocumentLineage, DocumentVersion } from "@/types/chat";

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getDocIcon(docType: string) {
  switch (docType) {
    case "docx":
    case "doc":
      return FileText;
    case "xlsx":
    case "xls":
    case "csv":
      return FileSpreadsheet;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "svg":
      return FileImage;
    default:
      return File;
  }
}

// ─── Component ────────────────────────────────────────────────────────────

/**
 * Side panel showing the version history of a document lineage.
 *
 * Subscribes to `openDocumentVersionPanel()` events via the pub/sub
 * system in `documentVersionEvents.ts`. Fetches lineage data from the
 * backend and renders a list of versions with download/preview actions.
 */
export function DocumentVersionPanel() {
  const [request, setRequest] = useState<VersionPanelRequest | null>(null);
  const [lineage, setLineage] = useState<DocumentLineage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to version panel open requests
  useEffect(() => {
    return subscribeVersionPanel((req) => {
      if (!req) {
        handleClose();
        return;
      }
      // If clicking the same lineage again, close the panel
      if (request?.lineageUuid === req.lineageUuid) {
        handleClose();
        return;
      }
      setRequest(req);
      setLineage(null);
      setError(null);
      setLoading(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.lineageUuid]);

  // Fetch lineage data when a request comes in
  useEffect(() => {
    if (!request || !loading) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get<{ data: DocumentLineage }>(
          `/chat/sessions/${request.sessionUuid}/documents/${request.lineageUuid}`,
        );
        if (cancelled) return;
        setLineage(res.data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load document lineage:", err);
        setError(err instanceof Error ? err.message : "Failed to load version history");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [request, loading]);

  const handleClose = useCallback(() => {
    setRequest(null);
    setLineage(null);
    setLoading(false);
    setError(null);
  }, []);

  const handleDownload = useCallback(async (version: DocumentVersion) => {
    try {
      const baseUrl = getApiBaseUrl();
      const headers = await getHeaders();
      headers.delete("Content-Type");

      const response = await fetch(`${baseUrl}/chat/attachments/${version.attachmentUuid}/file`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = version.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file:", err);
    }
  }, []);

  const handlePreview = useCallback((version: DocumentVersion) => {
    openDocumentPreview(version.attachmentUuid, version.filename);
  }, []);

  if (!request) return null;

  const DocIcon = lineage ? getDocIcon(lineage.documentType) : File;

  return (
    <div className="w-[360px] flex flex-col border-l border-border-primary bg-surface-primary min-w-0 overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary bg-surface-secondary shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 text-brand-secondary shrink-0" />
          <span className="text-sm text-content-primary font-medium truncate">Version History</span>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors shrink-0"
          title="Close version history"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
            <span className="text-sm text-content-secondary">Loading version history...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
            <FileWarning className="w-6 h-6 text-amber-400" />
            <span className="text-sm text-content-secondary max-w-md text-center">{error}</span>
          </div>
        )}

        {lineage && !loading && !error && (
          <div className="flex flex-col">
            {/* Document summary */}
            <div className="px-4 py-3 border-b border-border-primary">
              <div className="flex items-center gap-2 mb-1">
                <DocIcon className="w-4 h-4 text-brand-secondary" />
                <span className="text-sm font-medium text-content-primary truncate">{lineage.canonicalFilename}</span>
              </div>
              <span className="text-xs text-content-tertiary">
                {lineage.currentVersion} version{lineage.currentVersion !== 1 ? "s" : ""} &middot;{" "}
                {lineage.documentType.toUpperCase()}
              </span>
            </div>

            {/* Version list — newest first */}
            <div className="flex flex-col">
              {[...lineage.versions].reverse().map((version) => {
                const canPreview = isPreviewableFile(version.filename);
                const isCurrent = version.version === lineage.currentVersion;

                return (
                  <div
                    key={version.attachmentUuid}
                    className={`px-4 py-3 border-b border-border-primary hover:bg-surface-secondary transition-colors ${
                      isCurrent ? "bg-brand-secondary-subtle/30" : ""
                    }`}
                  >
                    {/* Version header */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
                            isCurrent
                              ? "bg-brand-secondary/20 text-brand-secondary"
                              : "bg-surface-tertiary text-content-tertiary"
                          }`}
                        >
                          v{version.version}
                        </span>
                        {isCurrent && <span className="text-[11px] text-brand-secondary font-medium">Current</span>}
                      </div>
                      <span className="text-[11px] text-content-quaternary">{formatTimestamp(version.createdAt)}</span>
                    </div>

                    {/* Filename + size */}
                    <div className="text-xs text-content-secondary mb-1 truncate">
                      {version.filename}
                      <span className="text-content-quaternary ml-1">({formatFileSize(version.size)})</span>
                    </div>

                    {/* Change summary */}
                    {version.changeSummary && (
                      <p className="text-xs text-content-tertiary mb-2 line-clamp-2">{version.changeSummary}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {canPreview && (
                        <button
                          type="button"
                          onClick={() => handlePreview(version)}
                          className="inline-flex items-center gap-1 text-[11px] text-brand-primary hover:text-brand-primary/80 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Preview
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownload(version)}
                        className="inline-flex items-center gap-1 text-[11px] text-brand-primary hover:text-brand-primary/80 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
